import { getAccessToken } from './auth'
import { getDatabricksBaseUrl } from './databricksHost'

export interface GenieMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

async function genieBaseFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken()
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(
      `Genie API error: ${response.status} ${response.statusText}${body ? ` — ${body}` : ''}`
    )
  }

  return response.json() as Promise<T>
}

function genieSpacesUrl(): string {
  return `${getDatabricksBaseUrl()}/api/2.0/genie/spaces`
}

export interface GenieSpace {
  space_id: string
  title?: string
  display_name?: string
  description?: string
  creator_id?: number
  workspace_id?: string
  state?: 'AVAILABLE' | 'PENDING' | 'DELETED' | string
}

export interface ListGenieSpacesResponse {
  spaces?: GenieSpace[]
  next_page_token?: string
}

export async function listGenieSpaces(): Promise<GenieSpace[]> {
  const data = await genieBaseFetch<ListGenieSpacesResponse>(genieSpacesUrl())
  return data.spaces ?? []
}

export function isGenieSpaceAvailable(space: GenieSpace): boolean {
  return !space.state || space.state === 'AVAILABLE'
}

/**
 * Build a direct link to a Genie space in the Databricks workspace UI (as
 * opposed to `getDatabricksBaseUrl()`, which is only the server-side proxy
 * path used for REST calls and isn't a browsable URL).
 */
export function getGenieSpaceUrl(spaceId: string): string | null {
  const host = import.meta.env.VITE_DATABRICKS_HOST as string | undefined
  if (!host) return null
  return `${host.replace(/\/$/, '')}/genie/rooms/${spaceId}`
}

/**
 * URL to load in the embedded (iframe) Genie experience for a given agent.
 *
 * This is deliberately NOT `getGenieSpaceUrl()`. That builds the console route
 * (`/genie/rooms/{id}`), which redirects to login and is served with framing
 * headers that block embedding -- Databricks protects the whole workspace SPA
 * from being framed. The dedicated embed surface at `/embed/genie/rooms/{id}`
 * responds 200 with `frame-ancestors *` instead, subject to the workspace's
 * AI/BI embedding policy.
 */
export function getGenieEmbedUrl(spaceId: string): string | null {
  const host = import.meta.env.VITE_DATABRICKS_HOST as string | undefined
  if (!host) return null
  return `${host.replace(/\/$/, '')}/embed/genie/rooms/${spaceId}`
}

// ---------------------------------------------------------------------------
// Genie Conversation API
//
// Genie processes each question asynchronously: starting a conversation or
// sending a follow-up message returns immediately with a message ID, and the
// caller must poll GET .../messages/{message_id} until the message's status
// reaches a terminal state (COMPLETED / FAILED / CANCELLED / ...).
// https://docs.databricks.com/api/workspace/genie
// ---------------------------------------------------------------------------

export type GenieMessageStatus =
  | 'IN_PROGRESS'
  | 'FETCHING_METADATA'
  | 'FILTERING_CONTEXT'
  | 'ASKING_AI'
  | 'EXECUTING_QUERY'
  | 'SUBMITTED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'QUERY_RESULT_EXPIRED'
  | string

export interface GenieAttachment {
  attachment_id?: string
  text?: { content: string }
  query?: {
    query?: string
    description?: string
    statement_id?: string
    query_result_metadata?: { row_count?: number }
  }
  suggested_questions?: { questions?: string[] }
}

export interface GenieConversationMessage {
  id: string
  conversation_id: string
  content?: string
  status: GenieMessageStatus
  attachments?: GenieAttachment[]
  query_result?: { statement_id?: string; row_count?: number }
  error?: { message?: string; type?: string }
}

const TERMINAL_STATUSES: GenieMessageStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'QUERY_RESULT_EXPIRED',
]

export function isGenieMessageTerminal(status: GenieMessageStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/** Extract the best available text summary of a completed Genie message. */
export function extractGenieReplyText(
  message: GenieConversationMessage
): string {
  const textAttachment = message.attachments?.find((a) => a.text?.content)
  if (textAttachment?.text?.content) {
    return textAttachment.text.content
  }

  const queryAttachment = message.attachments?.find((a) => a.query)
  if (queryAttachment?.query) {
    return (
      queryAttachment.query.description ||
      (queryAttachment.query.query
        ? `Generated query:\n${queryAttachment.query.query}`
        : 'Genie generated a query for this question.')
    )
  }

  if (message.content) {
    return message.content
  }

  if (message.error?.message) {
    return `Genie could not answer this question: ${message.error.message}`
  }

  return 'Genie did not return a text response for this question.'
}

export interface GenieQueryInfo {
  query?: string
  description?: string
  statementId: string
}

/** Find the SQL query Genie ran for this message, if any. */
export function findGenieQueryAttachment(
  message: GenieConversationMessage
): GenieQueryInfo | null {
  const attachment = message.attachments?.find((a) => a.query?.statement_id)
  if (attachment?.query?.statement_id) {
    return {
      query: attachment.query.query,
      description: attachment.query.description,
      statementId: attachment.query.statement_id,
    }
  }

  if (message.query_result?.statement_id) {
    return { statementId: message.query_result.statement_id }
  }

  return null
}

// ---------------------------------------------------------------------------
// SQL Statement Execution API — used to fetch the actual result rows for a
// query Genie ran, so the UI can render a table and/or chart in addition to
// Genie's text summary.
// https://docs.databricks.com/api/workspace/statementexecution
// ---------------------------------------------------------------------------

export interface GenieResultColumn {
  name: string
  typeName?: string
}

export interface GenieQueryResultData {
  columns: GenieResultColumn[]
  rows: string[][]
  totalRowCount: number
  truncated: boolean
}

interface StatementChunk {
  chunk_index?: number
  row_offset?: number
  row_count?: number
  data_array?: string[][]
}

interface StatementResultResponse {
  statement_id: string
  status: { state: string; error?: { message?: string } }
  manifest?: {
    schema?: { columns?: Array<{ name: string; type_name?: string }> }
    total_chunk_count?: number
    total_row_count?: number
    truncated?: boolean
  }
  result?: StatementChunk
}

function statementUrl(statementId: string, path = ''): string {
  return `${getDatabricksBaseUrl()}/api/2.0/sql/statements/${statementId}${path}`
}

async function fetchStatementResult(
  statementId: string
): Promise<StatementResultResponse & { rows: string[][] }> {
  const data = await genieBaseFetch<StatementResultResponse>(
    statementUrl(statementId)
  )

  if (data.status.state === 'FAILED') {
    throw new Error(
      data.status.error?.message || 'The generated query failed to execute.'
    )
  }

  if (data.status.state !== 'SUCCEEDED') {
    throw new Error(
      `The query result is not ready yet (status: ${data.status.state}).`
    )
  }

  const rows: string[][] = [...(data.result?.data_array ?? [])]

  const totalChunks = data.manifest?.total_chunk_count ?? 1
  for (let chunkIndex = 1; chunkIndex < totalChunks; chunkIndex++) {
    const chunk = await genieBaseFetch<StatementChunk>(
      statementUrl(statementId, `/result/chunks/${chunkIndex}`)
    )
    if (chunk.data_array) {
      rows.push(...chunk.data_array)
    }
  }

  return { ...data, rows }
}

export async function getGenieQueryResult(
  statementId: string
): Promise<GenieQueryResultData> {
  const data = await fetchStatementResult(statementId)

  const columns = (data.manifest?.schema?.columns ?? []).map((c) => ({
    name: c.name,
    typeName: c.type_name,
  }))

  return {
    columns,
    rows: data.rows,
    totalRowCount: data.manifest?.total_row_count ?? data.rows.length,
    truncated: Boolean(data.manifest?.truncated),
  }
}

/**
 * Raw shape expected by @databricks/appkit-ui's <GenieQueryVisualization>
 * component (a subset of the SQL Statement Execution API response, with any
 * paginated chunks merged into a single data_array).
 */
export interface GenieAppkitStatementResponse {
  manifest: {
    schema: {
      columns: Array<{ name: string; type_name: string }>
    }
  }
  result: {
    data_array: (string | null)[][]
  }
}

export async function getGenieAppkitStatementResponse(
  statementId: string
): Promise<GenieAppkitStatementResponse> {
  const data = await fetchStatementResult(statementId)

  return {
    manifest: {
      schema: {
        columns: (data.manifest?.schema?.columns ?? []).map((c) => ({
          name: c.name,
          type_name: c.type_name ?? '',
        })),
      },
    },
    result: {
      data_array: data.rows,
    },
  }
}

interface StartConversationResponse {
  conversation_id: string
  message_id?: string
  message?: { id?: string }
}

export interface GenieConversationHandle {
  conversationId: string
  messageId: string
}

export async function startGenieConversation(
  spaceId: string,
  content: string
): Promise<GenieConversationHandle> {
  const data = await genieBaseFetch<StartConversationResponse>(
    `${genieSpacesUrl()}/${spaceId}/start-conversation`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  )

  const messageId = data.message_id ?? data.message?.id
  if (!messageId) {
    throw new Error('Genie did not return a message ID for this conversation.')
  }

  return { conversationId: data.conversation_id, messageId }
}

interface SendMessageResponse {
  id?: string
  message_id?: string
}

export async function sendGenieMessage(
  spaceId: string,
  conversationId: string,
  content: string
): Promise<string> {
  const data = await genieBaseFetch<SendMessageResponse>(
    `${genieSpacesUrl()}/${spaceId}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  )

  const messageId = data.id ?? data.message_id
  if (!messageId) {
    throw new Error('Genie did not return a message ID for this message.')
  }

  return messageId
}

export async function getGenieMessage(
  spaceId: string,
  conversationId: string,
  messageId: string
): Promise<GenieConversationMessage> {
  return genieBaseFetch<GenieConversationMessage>(
    `${genieSpacesUrl()}/${spaceId}/conversations/${conversationId}/messages/${messageId}`
  )
}

// ---------------------------------------------------------------------------
// Past chats -- list a space's prior conversations and retrieve their full
// message history, so users can pick up a previous chat instead of starting
// fresh every time.
// https://docs.databricks.com/api/workspace/genie
// ---------------------------------------------------------------------------

export interface GenieConversationSummary {
  id: string
  spaceId: string
  title?: string
  createdAt?: number
  lastUpdatedAt?: number
}

interface RawGenieConversation {
  id?: string
  conversation_id?: string
  space_id?: string
  title?: string
  created_timestamp?: number
  last_updated_timestamp?: number
}

interface ListGenieConversationsResponse {
  conversations?: RawGenieConversation[]
  next_page_token?: string
}

/** List a Genie space's past conversations, most recently updated first. */
export async function listGenieConversations(
  spaceId: string
): Promise<GenieConversationSummary[]> {
  const data = await genieBaseFetch<ListGenieConversationsResponse>(
    `${genieSpacesUrl()}/${spaceId}/conversations`
  )

  return (data.conversations ?? [])
    .map((c) => ({
      id: c.id ?? c.conversation_id ?? '',
      spaceId: c.space_id ?? spaceId,
      title: c.title,
      createdAt: c.created_timestamp,
      lastUpdatedAt: c.last_updated_timestamp,
    }))
    .filter((c) => c.id)
    .sort(
      (a, b) =>
        (b.lastUpdatedAt ?? b.createdAt ?? 0) -
        (a.lastUpdatedAt ?? a.createdAt ?? 0)
    )
}

interface ListGenieConversationMessagesResponse {
  messages?: GenieConversationMessage[]
  next_page_token?: string
}

/**
 * Fetch every message in a past conversation, in the order they were sent.
 * Each message represents one full turn (the user's question plus, once
 * COMPLETED, Genie's response in the same object) -- see
 * extractGenieReplyText / findGenieQueryAttachment to render each turn.
 */
export async function listGenieConversationMessages(
  spaceId: string,
  conversationId: string
): Promise<GenieConversationMessage[]> {
  const data = await genieBaseFetch<ListGenieConversationMessagesResponse>(
    `${genieSpacesUrl()}/${spaceId}/conversations/${conversationId}/messages`
  )
  return data.messages ?? []
}
